import { useSyncExternalStore } from 'react';
import { X } from 'lucide-react';
import { tr } from '../i18n/i18n.ts';
import { localizeError } from '../i18n/format.ts';

export interface Toast {
  id: number;
  kind: 'ok' | 'error' | 'info' | 'warn';
  text: string;
  action?: { label: string; run: () => void | Promise<void> };
  /** ms; 0 = tills användaren stänger. Fel stannar kvar. */
  timeout: number;
}

let toasts: Toast[] = [];
let seq = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function toast(t: Omit<Toast, 'id' | 'timeout'> & { timeout?: number }): number {
  const id = seq++;
  const timeout = t.timeout ?? (t.kind === 'error' ? 0 : t.action ? 10000 : 5000);
  // Högst två samtidigt – äldre ersätts så att innehållet inte täcks.
  toasts = [...toasts.slice(-1), { ...t, id, timeout }];
  emit();
  if (timeout) setTimeout(() => dismiss(id), timeout);
  return id;
}
export function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/** Felmeddelande på valt språk. Domänens och serverns svenska meddelanden översätts när de är kända. */
export function errorText(e: unknown): string {
  return localizeError(e instanceof Error ? e.message : String(e));
}

export function ToastRegion() {
  const list = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => toasts,
  );
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-3 lg:bottom-6" aria-live="polite" aria-relevant="additions">
      {list.map((t) => (
        <div
          key={t.id}
          role={t.kind === 'error' ? 'alert' : 'status'}
          className={`pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-lg ${
            t.kind === 'error' ? 'border-risk/60 bg-risk-bg text-text' : t.kind === 'warn' ? 'border-warn/50 bg-warn-bg text-text' : 'border-border-strong bg-raised text-text'
          }`}
        >
          <span className="flex-1 leading-snug">{t.text}</span>
          {t.action && (
            <button
              className="min-h-11 rounded-lg px-3 font-semibold text-accent hover:bg-panel"
              onClick={async () => {
                dismiss(t.id);
                await t.action!.run();
              }}
            >
              {t.action.label}
            </button>
          )}
          <button className="grid min-h-11 min-w-11 place-items-center rounded-lg text-muted hover:text-text" onClick={() => dismiss(t.id)} aria-label={tr('Stäng meddelande', 'Close message')}>
            <X size={18} aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}
